/**
 * SettingsPaymentProviderController — onglet « Encaissement » de
 * /parametres.html (EPIC-004 / 4.5).
 *
 * Hydrate l'onglet à partir de GET /organization/settings/payment-provider :
 *   - statusSummary : pill `not_configured | active | error` + texte d'invitation
 *   - defaultsSummary : récap des 3 toggles + % acompte
 *   - boutons / inputs des deux nm-edit-block (statut + défauts)
 *
 * Gère les soumissions :
 *   - submitConnect — PUT { action:'connect', credentials, … }
 *   - disconnect   — PUT { action:'disconnect' }
 *   - submitDefaults — PUT { action:'update', toggles + % acompte }
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : toutes les variantes de pill statut +
 *     résumé défauts vivent dans des <template> (`tpl-wero-summary-*`,
 *     `tpl-wero-defaults-summary`) clonées via cloneTemplate / fillSlots.
 *   - Aucune classe CSS inventée.
 *   - Vocabulaire UI : « Wero » est explicitement visible.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class SettingsPaymentProviderController extends BaseController {
  static targets = [
    "blockStatut",
    "blockDefauts",
    "statusSummary",
    "connectForm",
    "credentialsInput",
    "connectSubmit",
    "disconnectButton",
    "defaultsSummary",
    "defaultsForm",
    "includeOnInvoicesInput",
    "includeOnQuotesInput",
    "depositPctInput",
    "autoInvoiceInput",
    "defaultsSubmit",
  ];

  async connect() {
    try {
      const data = await api.getPaymentProviderSettings();
      this.hydrateFromApi(data);
    } catch (_error) {
      // 401 déjà redirigé par api.js. Pour toute autre erreur, on garde la
      // maquette par défaut (not_configured) et on alerte l'utilisateur.
      this.showError("Impossible de charger les paramètres d'encaissement.");
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  hydrateFromApi(data) {
    if (!data || typeof data !== "object") return;
    this._renderStatusSummary(data.status);
    this._renderDefaultsSummary(data);
    this._populateForms(data);
    this._toggleDisconnectVisibility(data.status === "active");
  }

  _renderStatusSummary(status) {
    if (!this.hasStatusSummaryTarget) return;
    const tplId = status === "active"
      ? "tpl-wero-summary-active"
      : status === "error"
        ? "tpl-wero-summary-error"
        : "tpl-wero-summary-not-configured";
    const fragment = this.cloneTemplate(tplId);
    while (this.statusSummaryTarget.firstChild) {
      this.statusSummaryTarget.removeChild(this.statusSummaryTarget.firstChild);
    }
    this.statusSummaryTarget.appendChild(fragment);
  }

  _renderDefaultsSummary(data) {
    if (!this.hasDefaultsSummaryTarget) return;
    const fragment = this.cloneTemplate("tpl-wero-defaults-summary");
    this.fillSlots(fragment, {
      invoicesLabel: data.includeOnInvoices
        ? "ajouté aux factures émises"
        : "non ajouté aux factures",
      depositLabel: this._formatDepositPct(data.quoteDepositPct, data.includeOnQuotes),
      autoInvoiceLabel: data.autoInvoiceOnPayment
        ? "émise automatiquement"
        : "non émise automatiquement",
    });
    while (this.defaultsSummaryTarget.firstChild) {
      this.defaultsSummaryTarget.removeChild(this.defaultsSummaryTarget.firstChild);
    }
    this.defaultsSummaryTarget.appendChild(fragment);
  }

  _populateForms(data) {
    if (this.hasIncludeOnInvoicesInputTarget) {
      this.includeOnInvoicesInputTarget.checked = Boolean(data.includeOnInvoices);
    }
    if (this.hasIncludeOnQuotesInputTarget) {
      this.includeOnQuotesInputTarget.checked = Boolean(data.includeOnQuotes);
    }
    if (this.hasDepositPctInputTarget) {
      // L'API renvoie une string décimale ("30.00"). On reformate en entier
      // simple pour l'input number sans créer de bruit visuel.
      const num = Number.parseFloat(data.quoteDepositPct);
      this.depositPctInputTarget.value = Number.isFinite(num)
        ? String(Math.round(num * 100) / 100)
        : "30";
    }
    if (this.hasAutoInvoiceInputTarget) {
      this.autoInvoiceInputTarget.checked = Boolean(data.autoInvoiceOnPayment);
    }
  }

  _toggleDisconnectVisibility(isActive) {
    if (!this.hasDisconnectButtonTarget) return;
    this.disconnectButtonTarget.hidden = !isActive;
  }

  _formatDepositPct(rawPct, includeOnQuotes) {
    const num = Number.parseFloat(rawPct);
    if (!Number.isFinite(num)) return "—";
    if (!includeOnQuotes) {
      return "désactivé sur les devis";
    }
    // Affichage utilisateur sans décimales superflues (30 % plutôt que 30.00 %).
    const rounded = Math.round(num * 100) / 100;
    const display = Number.isInteger(rounded) ? String(rounded) : String(rounded);
    return display + " %";
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  //
  // EPIC-004 / 4.6 : la page n'embarque plus `maquettes/shared/edit-block.js`
  // (anciennement utilisée pour la revue maquette). Les transitions
  // summary ↔ form sont portées directement par les méthodes ci-dessous.
  // ---------------------------------------------------------------------------

  openStatutBlock(event) {
    event.preventDefault();
    if (this.hasBlockStatutTarget) this._openBlock(this.blockStatutTarget);
  }

  cancelStatutBlock(event) {
    event.preventDefault();
    if (this.hasBlockStatutTarget) this._closeBlock(this.blockStatutTarget);
  }

  openDefautsBlock(event) {
    event.preventDefault();
    if (this.hasBlockDefautsTarget) this._openBlock(this.blockDefautsTarget);
  }

  cancelDefautsBlock(event) {
    event.preventDefault();
    if (this.hasBlockDefautsTarget) this._closeBlock(this.blockDefautsTarget);
  }

  _openBlock(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _closeBlock(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async submitConnect(event) {
    event.preventDefault();
    if (!this.hasCredentialsInputTarget) return;

    const credentials = this.credentialsInputTarget.value.trim();
    if (!credentials) {
      this.showError("Identifiant Wero requis.");
      return;
    }

    const payload = {
      action: "connect",
      credentials,
      includeOnInvoices: this.hasIncludeOnInvoicesInputTarget
        ? this.includeOnInvoicesInputTarget.checked
        : false,
      includeOnQuotes: this.hasIncludeOnQuotesInputTarget
        ? this.includeOnQuotesInputTarget.checked
        : false,
      quoteDepositPct: this._readDepositPct(),
      autoInvoiceOnPayment: this.hasAutoInvoiceInputTarget
        ? this.autoInvoiceInputTarget.checked
        : false,
    };

    await this._submit(
      payload,
      this.hasConnectSubmitTarget ? this.connectSubmitTarget : null,
      "Compte Wero connecté.",
      this.hasBlockStatutTarget ? this.blockStatutTarget : null,
    );
  }

  async disconnect() {
    const confirmed = await this.confirm(
      "Déconnecter votre compte Wero ? Les liens déjà émis restent valides — seuls les nouveaux documents ne porteront plus de lien Wero.",
      { confirmLabel: "Déconnecter", cancelLabel: "Annuler" },
    );
    if (!confirmed) return;

    await this._submit(
      { action: "disconnect" },
      this.hasDisconnectButtonTarget ? this.disconnectButtonTarget : null,
      "Compte Wero déconnecté.",
      this.hasBlockStatutTarget ? this.blockStatutTarget : null,
    );
  }

  async submitDefaults(event) {
    event.preventDefault();

    const payload = {
      action: "update",
      includeOnInvoices: this.hasIncludeOnInvoicesInputTarget
        ? this.includeOnInvoicesInputTarget.checked
        : false,
      includeOnQuotes: this.hasIncludeOnQuotesInputTarget
        ? this.includeOnQuotesInputTarget.checked
        : false,
      quoteDepositPct: this._readDepositPct(),
      autoInvoiceOnPayment: this.hasAutoInvoiceInputTarget
        ? this.autoInvoiceInputTarget.checked
        : false,
    };

    await this._submit(
      payload,
      this.hasDefaultsSubmitTarget ? this.defaultsSubmitTarget : null,
      "Réglages d'encaissement enregistrés.",
      this.hasBlockDefautsTarget ? this.blockDefautsTarget : null,
    );
  }

  _readDepositPct() {
    if (!this.hasDepositPctInputTarget) return 30;
    const raw = Number.parseFloat(this.depositPctInputTarget.value);
    if (!Number.isFinite(raw)) return 30;
    return Math.max(0, Math.min(100, raw));
  }

  async _submit(payload, submitButton, successMessage, blockToClose = null) {
    if (submitButton) submitButton.disabled = true;
    try {
      const data = await api.updatePaymentProviderSettings(payload);
      this.hydrateFromApi(data);
      if (blockToClose) this._closeBlock(blockToClose);
      this.showSuccess(successMessage);
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour des paramètres d'encaissement.";
      this.showError(msg);
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }
}
